import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Fab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapIcon,
  Check as CheckIcon,
  EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
  DragIndicator as DragHandleIcon,
  CalendarMonth as CalendarMonthIcon,
  ContentCopy as ContentCopyIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  TrainingProgram,
  ProgramExercise,
  PlanTemplate,
  PlanTemplateExercise,
  PlanTemplateRotation,
  Exercise,
  PendingPlanProgram,
  RotationalConfig,
  AdaptationRecommendation,
  CalendarPlanSource,
  PlanTemplateSuggestedSet,
} from '../../services/types';
import { TrainingProgramService } from '../../services/trainingProgramService';
import { ProgramExerciseService } from '../../services/programExerciseService';
import { WorkoutPlanService } from '../../services/workoutPlanService';
import { ExerciseService } from '../../services/exerciseService';
import { RotationalTrainingService } from '../../services/rotationalTrainingService';
import ExercisePickerDialog from '../exercises/ExercisePickerDialog';

interface PlanSetInput {
  setOrder: number;
  targetReps: number;
  targetWeight: number;
  targetRpe: number | null;
  source: 'last_workout' | 'program' | 'calendar';
  notes?: string;
}

interface PlanExerciseInput {
  exerciseId: number;
  exerciseName: string;
  muscleGroup: string;
  isSubstitution: boolean;
  hasVariations?: boolean;
  parentExerciseId?: number | null;
  variationName?: string | null;
  sets: PlanSetInput[];
}

interface WorkoutPlannerProps {
  onDone?: () => void;
  initialProgramId?: number;
  initialDay?: number;
}

function SortableExerciseCard({ id, children }: { id: string; children: (dragHandleProps: { listeners?: Record<string, any>; attributes: Record<string, any> }) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes })}
    </div>
  );
}

const WorkoutPlanner: React.FC<WorkoutPlannerProps> = ({ onDone, initialProgramId, initialDay }) => {
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<TrainingProgram | null>(null);
  const [programDays, setProgramDays] = useState<{ [key: number]: ProgramExercise[] }>({});
  const [dayNames, setDayNames] = useState<{ [key: number]: string }>({});
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [planExercises, setPlanExercises] = useState<PlanExerciseInput[]>([]);
  const [plannedDate, setPlannedDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [planSourceMode, setPlanSourceMode] = useState<'new' | 'calendar' | null>(null);
  const [calendarSources, setCalendarSources] = useState<CalendarPlanSource[]>([]);
  const [loadingCalendarSources, setLoadingCalendarSources] = useState(false);
  const [selectedSourceSession, setSelectedSourceSession] = useState<CalendarPlanSource | null>(null);
  const [expandedSourceId, setExpandedSourceId] = useState<number | null>(null);
  const [sourcePreviewById, setSourcePreviewById] = useState<Record<number, PlanExerciseInput[]>>({});
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<AdaptationRecommendation[]>([]);
  const [recommendationLoading, setRecommendationLoading] = useState(false);

  // Pending status for filtering days
  const [pendingStatus, setPendingStatus] = useState<PendingPlanProgram | null>(null);

  // Rotation state
  const [rotationConfigs, setRotationConfigs] = useState<{ [dayNumber: number]: RotationalConfig }>({});
  const [currentRotation, setCurrentRotation] = useState<PlanTemplateRotation | null>(null);
  const [selectedRotationIndex, setSelectedRotationIndex] = useState<number | null>(null);
  const [rotationSwitching, setRotationSwitching] = useState(false);

  // Substitute dialog state
  const [substituteDialogOpen, setSubstituteDialogOpen] = useState(false);
  const [substituteExerciseIndex, setSubstituteExerciseIndex] = useState<number | null>(null);

  // Add exercise dialog state
  const [addExerciseDialogOpen, setAddExerciseDialogOpen] = useState(false);

  // Variation switcher state
  const [variationDialogOpen, setVariationDialogOpen] = useState(false);
  const [variationOptions, setVariationOptions] = useState<Exercise[]>([]);
  const [loadingVariationOptions, setLoadingVariationOptions] = useState(false);
  const [variationExerciseIndex, setVariationExerciseIndex] = useState<number>(0);

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    if (initialProgramId && programs.length > 0) {
      const program = programs.find(p => p.id === initialProgramId);
      if (program) {
        handleProgramSelect(program).then(() => {
          if (initialDay) {
            handleDaySelect(initialDay);
          }
        });
      }
    }
  }, [initialProgramId, initialDay, programs]);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const data = await TrainingProgramService.getAll();
      setPrograms(data);
    } catch (err) {
      setError('Грешка при зареждане на програмите');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingStatus = async (programId: number) => {
    try {
      const status = await WorkoutPlanService.getPendingStatus();
      const programStatus = status.programs.find(p => p.programId === programId);
      setPendingStatus(programStatus || null);
    } catch {
      // Non-critical, don't block the flow
    }
  };

  const mapTemplateExercises = (templateExercises: PlanTemplateExercise[]): PlanExerciseInput[] =>
    templateExercises.map((ex: PlanTemplateExercise) => ({
      exerciseId: ex.exerciseId,
      exerciseName: ex.exerciseName,
      muscleGroup: ex.muscleGroup,
      isSubstitution: Boolean((ex as PlanTemplateExercise & { isSubstitution?: boolean }).isSubstitution),
      hasVariations: ex.hasVariations,
      parentExerciseId: ex.parentExerciseId,
      variationName: ex.variationName,
      sets: ex.suggestedSets.map((s: PlanTemplateSuggestedSet) => ({
        setOrder: s.setOrder,
        targetReps: s.targetReps,
        targetWeight: s.targetWeight ?? 0,
        targetRpe: s.targetRpe,
        source: s.source,
      })),
    }));

  const handleProgramSelect = async (program: TrainingProgram) => {
    try {
      setLoading(true);
      setSelectedProgram(program);
      setSelectedDay(null);
      setPlanExercises([]);
      setRecommendations([]);

      const [response] = await Promise.all([
        ProgramExerciseService.getProgramExercises(program.id),
        loadPendingStatus(program.id),
        RotationalTrainingService.getAll(program.id).then(res => {
          const configMap: { [dayNumber: number]: RotationalConfig } = {};
          res.rotations.forEach(c => { configMap[c.dayNumber] = c; });
          setRotationConfigs(configMap);
        }).catch(() => {}),
      ]);
      setProgramDays(response.exercisesByDay);
      setDayNames(response.dayNames || {});
    } catch (err) {
      setError('Грешка при зареждане на упражненията');
    } finally {
      setLoading(false);
    }
  };

  const handleDaySelect = (day: number) => {
    setSelectedDay(day);
    setPlanExercises([]);
    setRecommendations([]);
    setCurrentRotation(null);
    setSelectedRotationIndex(null);
    setPlanSourceMode(null);
    setCalendarSources([]);
    setSelectedSourceSession(null);
    setExpandedSourceId(null);
    setSourcePreviewById({});
    setPreviewLoadingId(null);
    setError(null);
    setSuccess(null);
  };

  const loadNewPlanTemplate = async () => {
    const pid = selectedProgram?.id;
    if (!pid || selectedDay === null) return;

    try {
      setLoading(true);
      setPlanSourceMode('new');
      setRecommendations([]);
      setSelectedSourceSession(null);

      // Load template from API
      const template: PlanTemplate = await WorkoutPlanService.getTemplate(pid, selectedDay);

      // Store rotation info if present
      setCurrentRotation(template.rotation || null);
      if (template.rotation) {
        setSelectedRotationIndex(template.rotation.recommendedIndex ?? template.rotation.currentIndex);
      } else {
        setSelectedRotationIndex(null);
      }

      setPlanExercises(mapTemplateExercises(template.exercises));

      // Review action generates fresh recommendations for the selected day.
      await WorkoutPlanService.reviewAdaptations(pid, selectedDay);
      const recommendationResponse = await WorkoutPlanService.getActiveRecommendations(pid, selectedDay);
      setRecommendations(recommendationResponse.recommendations);
    } catch (err) {
      setError('Грешка при зареждане на шаблона');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarSources = async () => {
    if (!selectedProgram || selectedDay === null) return;

    try {
      setLoadingCalendarSources(true);
      setError(null);
      setPlanSourceMode('calendar');
      setSelectedSourceSession(null);
      setPlanExercises([]);
      setRecommendations([]);
      setCurrentRotation(null);
      setSelectedRotationIndex(null);
      setExpandedSourceId(null);
      setSourcePreviewById({});
      setPreviewLoadingId(null);

      const response = await WorkoutPlanService.getCalendarSources(selectedProgram.id, selectedDay);
      setCalendarSources(response.sources);
    } catch {
      setError('Грешка при зареждане на тренировки от календара');
    } finally {
      setLoadingCalendarSources(false);
    }
  };

  const toggleCalendarSourcePreview = async (source: CalendarPlanSource) => {
    if (!selectedProgram || selectedDay === null) return;

    if (expandedSourceId === source.id) {
      setExpandedSourceId(null);
      return;
    }

    setExpandedSourceId(source.id);
    if (sourcePreviewById[source.id]) {
      return;
    }

    try {
      setPreviewLoadingId(source.id);
      setError(null);
      const template = await WorkoutPlanService.getFromSession(source.id, selectedProgram.id, selectedDay);
      setSourcePreviewById(prev => ({
        ...prev,
        [source.id]: mapTemplateExercises(template.exercises),
      }));
    } catch {
      setError('Грешка при зареждане на прегледа от календара');
      setExpandedSourceId(null);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const loadPlanFromCalendarSource = async (source: CalendarPlanSource) => {
    if (!selectedProgram || selectedDay === null) return;

    try {
      setLoading(true);
      setError(null);
      setSelectedSourceSession(source);
      setRecommendations([]);
      setCurrentRotation(null);
      setSelectedRotationIndex(null);

      const template = await WorkoutPlanService.getFromSession(source.id, selectedProgram.id, selectedDay);
      setPlanExercises(mapTemplateExercises(template.exercises));
    } catch {
      setError('Грешка при зареждане на тренировката от календара');
    } finally {
      setLoading(false);
    }
  };

  const applyRecommendationValues = (recommendation: AdaptationRecommendation) => {
    setPlanExercises(prev => prev.map(ex => {
      if (ex.exerciseId !== recommendation.exerciseId) {
        return ex;
      }

      const setCount = Math.max(1, recommendation.proposed.targetSets || ex.sets.length || 1);
      const nextSets: PlanSetInput[] = Array.from({ length: setCount }, (_, idx) => ({
        setOrder: idx + 1,
        targetReps: recommendation.proposed.targetReps ?? ex.sets[Math.min(idx, ex.sets.length - 1)]?.targetReps ?? 10,
        targetWeight: recommendation.proposed.targetWeight ?? ex.sets[Math.min(idx, ex.sets.length - 1)]?.targetWeight ?? 0,
        targetRpe: recommendation.proposed.targetRpe ?? null,
        source: 'program',
      }));

      return {
        ...ex,
        sets: nextSets,
      };
    }));
  };

  const handleRecommendationAction = async (
    recommendation: AdaptationRecommendation,
    status: 'accepted' | 'rejected'
  ) => {
    try {
      setRecommendationLoading(true);
      setError(null);

      const response = await WorkoutPlanService.updateRecommendationStatus(recommendation.id, status);
      const updated = response.recommendation;

      setRecommendations(prev =>
        prev.map(item => item.id === recommendation.id ? updated : item)
      );

      if (status === 'accepted') {
        applyRecommendationValues(updated);
      }
    } catch {
      setError('Грешка при обработка на препоръката. Опитай отново.');
    } finally {
      setRecommendationLoading(false);
    }
  };

  const adjustWeight = (exerciseIndex: number, setIndex: number, delta: number) => {
    setPlanExercises(prev => prev.map((ex, i) =>
      i !== exerciseIndex ? ex : {
        ...ex,
        sets: ex.sets.map((s, j) =>
          j !== setIndex ? s : { ...s, targetWeight: Math.max(0, s.targetWeight + delta) }
        ),
      }
    ));
  };

  const adjustReps = (exerciseIndex: number, setIndex: number, delta: number) => {
    setPlanExercises(prev => prev.map((ex, i) =>
      i !== exerciseIndex ? ex : {
        ...ex,
        sets: ex.sets.map((s, j) =>
          j !== setIndex ? s : { ...s, targetReps: Math.max(1, s.targetReps + delta) }
        ),
      }
    ));
  };

  const handleAddSet = (exerciseIndex: number) => {
    setPlanExercises(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex) return ex;
      const lastSet = ex.sets[ex.sets.length - 1];
      return {
        ...ex,
        sets: [...ex.sets, {
          setOrder: ex.sets.length + 1,
          targetReps: lastSet?.targetReps ?? 10,
          targetWeight: lastSet?.targetWeight ?? 0,
          targetRpe: null,
          source: 'program' as const,
        }],
      };
    }));
  };

  const handleRemoveSet = (exerciseIndex: number, setIndex: number) => {
    setPlanExercises(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex || ex.sets.length <= 1) return ex;
      const newSets = ex.sets.filter((_, j) => j !== setIndex)
        .map((s, idx) => ({ ...s, setOrder: idx + 1 }));
      return { ...ex, sets: newSets };
    }));
  };

  const handleRemoveExercise = (exerciseIndex: number) => {
    if (planExercises.length <= 1) return;
    setPlanExercises(prev => prev.filter((_, i) => i !== exerciseIndex));
  };

  // Drag & drop sensors for exercise cards
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } });
  const plannerSensors = useSensors(pointerSensor, touchSensor);

  const handleExerciseDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = planExercises.findIndex((_, i) => `plan-ex-${i}` === active.id);
    const newIndex = planExercises.findIndex((_, i) => `plan-ex-${i}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    setPlanExercises(prev => arrayMove(prev, oldIndex, newIndex));
  }, [planExercises]);

  const handleRotationSwitch = async (targetIndex: number) => {
    if (!selectedProgram || selectedDay === null || targetIndex === selectedRotationIndex) return;

    try {
      setRotationSwitching(true);
      setError(null);

      // Persist the choice so the rotation cycle stays consistent
      await RotationalTrainingService.setIndex(selectedProgram.id, selectedDay, targetIndex);

      // Reload template exercises for the chosen rotation
      const template = await WorkoutPlanService.getTemplate(selectedProgram.id, selectedDay, targetIndex);

      setCurrentRotation(template.rotation || null);
      setSelectedRotationIndex(targetIndex);

      const exercises: PlanExerciseInput[] = template.exercises.map((ex: PlanTemplateExercise) => ({
        exerciseId: ex.exerciseId,
        exerciseName: ex.exerciseName,
        muscleGroup: ex.muscleGroup,
        isSubstitution: false,
        sets: ex.suggestedSets.map(s => ({
          setOrder: s.setOrder,
          targetReps: s.targetReps,
          targetWeight: s.targetWeight ?? 0,
          targetRpe: s.targetRpe,
          source: s.source,
        })),
      }));
      setPlanExercises(exercises);
    } catch (err) {
      setError('Грешка при смяна на варианта');
    } finally {
      setRotationSwitching(false);
    }
  };

  const handleOpenSubstitute = (exerciseIndex: number) => {
    setSubstituteExerciseIndex(exerciseIndex);
    setSubstituteDialogOpen(true);
  };

  const handleConfirmSubstitute = async (exercise: Exercise) => {
    if (substituteExerciseIndex === null) return;

    // Fetch last workout data for the substitute exercise
    const lastWorkoutData = await ExerciseService.getLastWorkoutData(exercise.id);

    setPlanExercises(prev => prev.map((ex, i) => {
      if (i !== substituteExerciseIndex) return ex;

      // Update sets with last workout data if available
      const newSets = ex.sets.map((s, idx) => {
        const lastWeight = lastWorkoutData?.sets?.[idx]?.weight;
        return {
          ...s,
          targetWeight: typeof lastWeight === 'number' ? lastWeight : s.targetWeight,
          source: typeof lastWeight === 'number' ? 'last_workout' as const : 'program' as const,
        };
      });

      return {
        ...ex,
        exerciseId: exercise.id,
        exerciseName: exercise.name,
        muscleGroup: exercise.muscleGroup,
        isSubstitution: true,
        sets: newSets,
      };
    }));

    setSubstituteDialogOpen(false);
    setSubstituteExerciseIndex(null);
  };

  const handleOpenAddExercise = () => {
    setAddExerciseDialogOpen(true);
  };

  const handleConfirmAddExercise = async (exercise: Exercise) => {
    let sets: PlanSetInput[] = Array.from({ length: 3 }, (_, idx) => ({
      setOrder: idx + 1,
      targetReps: 10,
      targetWeight: 0,
      targetRpe: 7,
      source: 'program' as const,
    }));

    try {
      const lastWorkoutData = await ExerciseService.getLastWorkoutData(exercise.id);
      if (lastWorkoutData?.sets?.length) {
        sets = sets.map((s, idx) => {
          const lastSet = lastWorkoutData.sets[idx];
          const lastWeight = typeof lastSet?.weight === 'number' ? lastSet.weight : null;
          if (lastWeight !== null) {
            return { ...s, targetWeight: lastWeight, source: 'last_workout' as const };
          }
          return s;
        });
      }
    } catch {
      // Keep defaults if last workout fetch fails
    }

    const newExercise: PlanExerciseInput = {
      exerciseId: exercise.id,
      exerciseName: exercise.name,
      muscleGroup: exercise.muscleGroup,
      isSubstitution: false,
      hasVariations: exercise.hasVariations,
      parentExerciseId: exercise.parentExerciseId ?? null,
      variationName: exercise.variationName ?? null,
      sets,
    };

    setPlanExercises(prev => [...prev, newExercise]);
    setAddExerciseDialogOpen(false);
  };

  const handleOpenVariationSwitcher = async (exerciseIndex: number) => {
    setVariationExerciseIndex(exerciseIndex);
    const ex = planExercises[exerciseIndex];
    const baseId = ex.parentExerciseId ?? ex.exerciseId;
    setLoadingVariationOptions(true);
    setVariationDialogOpen(true);
    try {
      const data = await ExerciseService.getVariations(baseId);
      setVariationOptions([data.baseExercise, ...data.variations]);
    } catch (err) {
      console.error('Failed to load variations:', err);
      setVariationOptions([]);
    } finally {
      setLoadingVariationOptions(false);
    }
  };

  const handleSelectVariation = async (variation: Exercise) => {
    setVariationDialogOpen(false);
    setPlanExercises(prev => prev.map((ex, i) => {
      if (i !== variationExerciseIndex) return ex;
      return {
        ...ex,
        exerciseId: variation.id,
        exerciseName: variation.name,
        muscleGroup: variation.muscleGroup,
        hasVariations: variation.hasVariations,
        parentExerciseId: variation.parentExerciseId ?? null,
        variationName: variation.variationName ?? null,
      };
    }));
    try {
      const lastWorkoutData = await ExerciseService.getLastWorkoutData(variation.id);
      setPlanExercises(prev => prev.map((ex, i) => {
        if (i !== variationExerciseIndex) return ex;
        const newSets = ex.sets.map((s, idx) => {
          const lastSet = lastWorkoutData?.sets?.[idx];
          const lastWeight = typeof lastSet?.weight === 'number' ? lastSet.weight : null;
          if (lastWeight !== null) {
            return { ...s, targetWeight: lastWeight, source: 'last_workout' as const };
          }
          return s;
        });
        return { ...ex, sets: newSets };
      }));
    } catch {
      // Keep current set values if last workout fetch fails
    }
  };

  const handleSavePlan = async () => {
    if (!selectedProgram || selectedDay === null) return;

    try {
      setSaving(true);
      setError(null);

      const selectedLabel = currentRotation && selectedRotationIndex !== null
        ? currentRotation.rotationLabels[selectedRotationIndex] ?? currentRotation.currentLabel
        : currentRotation?.currentLabel;
      const rotationSuffix = selectedLabel ? ` (${selectedLabel})` : '';
      await WorkoutPlanService.create({
        name: `${selectedProgram.name} - ${dayNames[selectedDay] || `Ден ${selectedDay}`}${rotationSuffix}`,
        workoutDate: plannedDate,
        programId: selectedProgram.id,
        dayNumber: selectedDay,
        exercises: planExercises.map(ex => ({
          exerciseId: ex.exerciseId,
          isSubstitution: ex.isSubstitution || undefined,
          sets: ex.sets.map(s => ({
            setOrder: s.setOrder,
            targetReps: s.targetReps,
            targetWeight: s.targetWeight > 0 ? s.targetWeight : null,
            targetRpe: s.targetRpe,
          })),
        })),
      });

      setSuccess('Планът е запазен успешно!');

      // Refresh pending status and go back to day selection
      if (selectedProgram) {
        await loadPendingStatus(selectedProgram.id);
      }
      setTimeout(() => {
        setSelectedDay(null);
        setPlanExercises([]);
        setSuccess(null);
      }, 1500);
    } catch (err) {
      setError('Грешка при запазване на плана');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToSourceChoice = () => {
    setPlanExercises([]);
    setRecommendations([]);
    setCurrentRotation(null);
    setSelectedRotationIndex(null);
    setPlanSourceMode(null);
    setSelectedSourceSession(null);
    setError(null);
  };

  const sourceStatusLabel = (status: string) => {
    if (status === 'completed') return 'Завършена';
    if (status === 'planned') return 'Планирана';
    return status;
  };

  // Step 1: Program selection
  if (!selectedProgram) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <EventNoteIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">
            Планирай тренировка
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Избери програма за която искаш да създадеш план
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            {programs.filter(p => p.active).map(program => (
              <Card
                key={program.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                  transition: 'all 0.15s',
                }}
                onClick={() => handleProgramSelect(program)}
              >
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h6" fontWeight="bold">{program.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {program.description || 'Няма описание'}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip label={`${program.workoutsPerWeek} дни/седмица`} size="small" variant="outlined" />
                    <Chip label={program.difficulty} size="small" color="primary" variant="outlined" />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {programs.filter(p => p.active).length === 0 && (
              <Alert severity="info">Нямаш активни програми.</Alert>
            )}
          </Stack>
        )}
      </Box>
    );
  }

  // Step 2: Day selection
  if (selectedDay === null) {
    const days = Object.keys(programDays).map(Number).sort((a, b) => a - b);
    const availableDays = pendingStatus
      ? days.filter(d => pendingStatus.unplannedDays.includes(d))
      : days;
    const plannedDaysList = pendingStatus?.plannedDays || [];
    const inProgressDaysList = pendingStatus?.inProgressDays || [];
    const completedDaysList = pendingStatus?.completedDays || [];
    const allDone = pendingStatus && availableDays.length === 0;

    return (
      <Box sx={{ p: 2 }}>
        <Button onClick={() => { setSelectedProgram(null); setPendingStatus(null); }} sx={{ mb: 2 }}>
          &larr; Назад към програми
        </Button>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Избери ден
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {selectedProgram.name}
        </Typography>

        {pendingStatus && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {plannedDaysList.length + inProgressDaysList.length + completedDaysList.length} от {days.length} дни планирани/завършени тази седмица
            </Typography>
          </Box>
        )}

        {allDone ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Всички дни за тази седмица са планирани или завършени!
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => onDone?.()}>
                Към начало
              </Button>
            </Box>
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {availableDays.map(day => {
              const rotConfig = rotationConfigs[day];
              return (
                <Card
                  key={day}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 },
                    transition: 'all 0.15s',
                  }}
                  onClick={() => handleDaySelect(day)}
                >
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {dayNames[day] || `Ден ${day}`}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {programDays[day]?.length || 0} упражнения
                          </Typography>
                          {rotConfig && (
                            <Chip
                              label={`${rotConfig.rotationLabels[rotConfig.currentIndex]}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 22 }}
                            />
                          )}
                        </Stack>
                      </Box>
                      <EventNoteIcon color="primary" />
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}

        {/* Show planned/in-progress/completed days as disabled cards */}
        {(plannedDaysList.length > 0 || inProgressDaysList.length > 0 || completedDaysList.length > 0) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Вече планирани/завършени
            </Typography>
            <Stack spacing={1}>
              {inProgressDaysList.map(day => (
                <Card key={`inprogress-${day}`} sx={{ opacity: 0.5 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        {dayNames[day] || `Ден ${day}`}
                      </Typography>
                      <Chip label="В прогрес" size="small" color="warning" />
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {plannedDaysList.map(day => (
                <Card key={`planned-${day}`} sx={{ opacity: 0.5 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        {dayNames[day] || `Ден ${day}`}
                      </Typography>
                      <Chip label="Планиран" size="small" color="info" icon={<EventNoteIcon />} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {completedDaysList.map(day => (
                <Card key={`completed-${day}`} sx={{ opacity: 0.5 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        {dayNames[day] || `Ден ${day}`}
                      </Typography>
                      <Chip label="Завършен" size="small" color="success" icon={<CheckCircleIcon />} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    );
  }

  // Step 3: Source selection
  if (planExercises.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Button onClick={() => setSelectedDay(null)} sx={{ mb: 2 }}>
          &larr; Назад към дни
        </Button>

        <Typography variant="h5" gutterBottom fontWeight="bold">
          Избери източник
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedProgram.name} - {dayNames[selectedDay] || `Ден ${selectedDay}`}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            label="Дата на тренировка"
            type="date"
            value={plannedDate}
            onChange={(e) => setPlannedDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Paper>

        <Paper sx={{ p: 0.75, mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.75,
            }}
          >
            <Button
              variant={planSourceMode === 'new' ? 'contained' : 'text'}
              color="primary"
              startIcon={loading && planSourceMode === 'new' ? <CircularProgress size={16} color="inherit" /> : <EventNoteIcon />}
              onClick={loadNewPlanTemplate}
              disabled={loading}
              sx={{ py: 1.25, minHeight: 46 }}
            >
              Нова
            </Button>
            <Button
              variant={planSourceMode === 'calendar' ? 'contained' : 'text'}
              color="primary"
              startIcon={loadingCalendarSources ? <CircularProgress size={16} color="inherit" /> : <CalendarMonthIcon />}
              onClick={loadCalendarSources}
              disabled={loadingCalendarSources}
              sx={{ py: 1.25, minHeight: 46 }}
            >
              От календара
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 0.5 }}>
            Новата тренировка зарежда програмния шаблон. Календарът копира стара тренировка за редакция.
          </Typography>
        </Paper>

        {planSourceMode === 'calendar' && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Тренировки от календара
            </Typography>
            {loadingCalendarSources ? (
              <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
            ) : calendarSources.length === 0 ? (
              <Alert severity="info">
                Няма намерени тренировки от календара за този ден от програмата.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {calendarSources.map(source => {
                  const isExpanded = expandedSourceId === source.id;
                  const preview = sourcePreviewById[source.id] || [];
                  const isPreviewLoading = previewLoadingId === source.id;

                  return (
                  <Card key={source.id}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {source.name}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            <Chip label={source.workoutDate} size="small" variant="outlined" />
                            <Chip label={sourceStatusLabel(source.status)} size="small" color={source.status === 'completed' ? 'success' : 'info'} />
                            <Chip label={`${source.exerciseCount} упр.`} size="small" variant="outlined" />
                            <Chip label={`${source.setCount} сета`} size="small" variant="outlined" />
                          </Stack>
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ContentCopyIcon />}
                          disabled={loading}
                          onClick={() => loadPlanFromCalendarSource(source)}
                          sx={{ flexShrink: 0 }}
                        >
                          Зареди
                        </Button>
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        onClick={() => toggleCalendarSourcePreview(source)}
                        sx={{ mt: 1 }}
                      >
                        {isExpanded ? 'Скрий упражненията' : 'Покажи упражненията'}
                      </Button>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
                          {isPreviewLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                              <CircularProgress size={18} />
                              <Typography variant="body2" color="text.secondary">Зареждане...</Typography>
                            </Box>
                          ) : preview.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Няма данни за упражненията.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {preview.map((exercise, exerciseIndex) => (
                                <Box key={`${source.id}-${exercise.exerciseId}-${exerciseIndex}`}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {exerciseIndex + 1}. {exercise.exerciseName}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                    {exercise.sets.map(set => (
                                      <Chip
                                        key={set.setOrder}
                                        size="small"
                                        variant="outlined"
                                        label={`${set.setOrder}: ${set.targetReps} x ${set.targetWeight || 0} кг${set.targetRpe ? ` @${set.targetRpe}` : ''}`}
                                      />
                                    ))}
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
                })}
              </Stack>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Step 4: Plan editing
  return (
    <Box sx={{ pb: 10 }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Button onClick={handleBackToSourceChoice} size="small">
            &larr; Назад
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePlan}
            disabled={saving || planExercises.length === 0}
            size="small"
            color="success"
            startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Запази план
          </Button>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold">
            {selectedProgram.name} - {dayNames[selectedDay] || `Ден ${selectedDay}`}
          </Typography>
          {currentRotation && selectedRotationIndex !== null && (
            <Chip
              label={currentRotation.rotationLabels[selectedRotationIndex] ?? currentRotation.currentLabel}
              size="small"
              color="secondary"
            />
          )}
          {selectedSourceSession && (
            <Chip
              label={`От календар: ${selectedSourceSession.workoutDate}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 2, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Date picker */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <TextField
                label="Дата на тренировка"
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Paper>

            {/* Rotation picker – shown only when the day has multiple variants */}
            {currentRotation && currentRotation.rotationCount > 1 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {currentRotation.rotationName} — избери вариант:
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {currentRotation.rotationLabels.map((label, idx) => {
                    const isSelected = idx === selectedRotationIndex;
                    const isRecommended = idx === currentRotation.recommendedIndex;
                    return (
                      <Paper
                        key={idx}
                        variant={isSelected ? 'elevation' : 'outlined'}
                        elevation={isSelected ? 4 : 0}
                        onClick={() => !rotationSwitching && handleRotationSwitch(idx)}
                        data-testid={`rotation-variant-${idx}`}
                        sx={{
                          p: 1.5,
                          flex: '1 1 0',
                          minWidth: 110,
                          cursor: rotationSwitching ? 'wait' : 'pointer',
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'primary.contrastText' : 'text.primary',
                          transition: 'all 0.15s',
                          '&:hover': rotationSwitching ? {} : { boxShadow: 3 },
                          opacity: rotationSwitching && !isSelected ? 0.5 : 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          textAlign="center"
                          color="inherit"
                        >
                          {label}
                        </Typography>
                        {isRecommended && (
                          <Box sx={{ textAlign: 'center', mt: 0.75 }}>
                            <Chip
                              label="Препоръчано"
                              size="small"
                              color={isSelected ? 'default' : 'primary'}
                              variant="outlined"
                              sx={{
                                height: 18,
                                '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                                borderColor: isSelected ? 'primary.contrastText' : undefined,
                                color: isSelected ? 'primary.contrastText' : undefined,
                              }}
                            />
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
                {rotationSwitching && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}
              </Paper>
            )}

            {recommendations.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  Препоръчани адаптации
                </Typography>
                <Stack spacing={1.25}>
                  {recommendations.map((recommendation) => {
                    const hasWeightDelta = recommendation.current.targetWeight !== recommendation.proposed.targetWeight;
                    const hasRepsDelta = recommendation.current.targetReps !== recommendation.proposed.targetReps;
                    const hasSetsDelta = recommendation.current.targetSets !== recommendation.proposed.targetSets;

                    return (
                      <Paper key={recommendation.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75 }}>
                          {recommendation.exerciseName}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            color={hasSetsDelta ? 'warning' : 'default'}
                            label={`Серии: ${recommendation.current.targetSets} → ${recommendation.proposed.targetSets}`}
                          />
                          <Chip
                            size="small"
                            color={hasRepsDelta ? 'warning' : 'default'}
                            label={`Повт.: ${recommendation.current.targetReps} → ${recommendation.proposed.targetReps}`}
                          />
                          <Chip
                            size="small"
                            color={hasWeightDelta ? 'warning' : 'default'}
                            label={`Тежест: ${recommendation.current.targetWeight ?? '-'} → ${recommendation.proposed.targetWeight ?? '-'}`}
                          />
                          <Chip
                            size="small"
                            color={recommendation.status === 'pending' ? 'default' : recommendation.status === 'accepted' ? 'success' : 'error'}
                            label={recommendation.status}
                          />
                        </Stack>
                        {recommendation.status === 'pending' ? (
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckIcon />}
                              disabled={recommendationLoading}
                              onClick={() => handleRecommendationAction(recommendation, 'accepted')}
                            >
                              Приеми
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CloseIcon />}
                              disabled={recommendationLoading}
                              onClick={() => handleRecommendationAction(recommendation, 'rejected')}
                            >
                              Отхвърли
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {recommendation.status === 'accepted'
                              ? 'Приета и приложима за бъдещо планиране.'
                              : 'Отхвърлена адаптация.'}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>
            )}

            {/* Exercise cards */}
            <DndContext sensors={plannerSensors} collisionDetection={closestCenter} onDragEnd={handleExerciseDragEnd}>
              <SortableContext items={planExercises.map((_, i) => `plan-ex-${i}`)} strategy={verticalListSortingStrategy}>
                <Stack spacing={3}>
                  {planExercises.map((exercise, exIndex) => (
                    <SortableExerciseCard key={`${exercise.exerciseId}-${exIndex}`} id={`plan-ex-${exIndex}`}>
                      {({ listeners, attributes }) => (
                        <Paper elevation={2} sx={{ p: 2 }}>
                          {/* Exercise header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0, flex: 1, gap: 1 }}>
                              <Box
                                {...listeners}
                                {...attributes}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'grab',
                                  touchAction: 'none',
                                  color: 'text.secondary',
                                  mt: 0.5,
                                  '&:active': { cursor: 'grabbing' },
                                }}
                              >
                                <DragHandleIcon />
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  {`${exIndex + 1}. ${exercise.exerciseName}`}
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  <Chip label={exercise.muscleGroup} size="small" color="primary" variant="outlined" />
                                  {exercise.isSubstitution && (
                                    <Chip label="Заменено" size="small" color="info" />
                                  )}
                                  {exercise.variationName && (
                                    <Chip label={exercise.variationName} size="small" color="secondary" variant="outlined" />
                                  )}
                                </Stack>
                              </Box>
                            </Box>
                            <Stack direction="row" alignItems="center">
                      {(exercise.hasVariations || exercise.parentExerciseId) && (
                        <IconButton
                          onClick={() => handleOpenVariationSwitcher(exIndex)}
                          size="small"
                          title="Смени вариация"
                          color="secondary"
                          aria-label="Смени вариация"
                        >
                          <TuneIcon />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleOpenSubstitute(exIndex)}
                        size="small"
                        title="Замени упражнение"
                        aria-label="Замени упражнение"
                      >
                        <SwapIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveExercise(exIndex)}
                        size="small"
                        color="error"
                        disabled={planExercises.length <= 1}
                        title="Премахни"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>

                  {/* Sets */}
                  <Stack spacing={1.5}>
                    {exercise.sets.map((set, setIndex) => (
                      <Box
                        key={setIndex}
                        sx={{
                          p: 1.5,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Сет {set.setOrder}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                              label={set.source === 'last_workout' ? 'Последна' : set.source === 'calendar' ? 'Календар' : 'Програма'}
                              size="small"
                              color={set.source === 'last_workout' ? 'success' : set.source === 'calendar' ? 'info' : 'default'}
                              variant="outlined"
                              sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                            />
                            {exercise.sets.length > 1 && (
                              <IconButton
                                onClick={() => handleRemoveSet(exIndex, setIndex)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </Box>

                        {/* Weight row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 55 }}>
                            Тежест
                          </Typography>
                          <Fab
                            size="small"
                            onClick={() => adjustWeight(exIndex, setIndex, -2.5)}
                            sx={{ minWidth: 36, minHeight: 36, width: 36, height: 36 }}
                          >
                            <RemoveIcon fontSize="small" />
                          </Fab>
                          <TextField
                            type="text"
                            inputMode="decimal"
                            value={set.targetWeight}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                              setPlanExercises(prev => prev.map((ex, i) =>
                                i !== exIndex ? ex : {
                                  ...ex,
                                  sets: ex.sets.map((s, j) =>
                                    j !== setIndex ? s : { ...s, targetWeight: val }
                                  ),
                                }
                              ));
                            }}
                            inputProps={{
                              style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 'bold' },
                            }}
                            sx={{ flex: 1, '& input': { py: 1 } }}
                            size="small"
                          />
                          <Fab
                            size="small"
                            onClick={() => adjustWeight(exIndex, setIndex, 2.5)}
                            sx={{ minWidth: 36, minHeight: 36, width: 36, height: 36 }}
                          >
                            <AddIcon fontSize="small" />
                          </Fab>
                          <Typography variant="caption" color="text.secondary">кг</Typography>
                        </Box>

                        {/* Reps row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 55 }}>
                            Повт.
                          </Typography>
                          <Fab
                            size="small"
                            color="secondary"
                            onClick={() => adjustReps(exIndex, setIndex, -1)}
                            sx={{ minWidth: 36, minHeight: 36, width: 36, height: 36 }}
                          >
                            <RemoveIcon fontSize="small" />
                          </Fab>
                          <TextField
                            type="text"
                            inputMode="numeric"
                            value={set.targetReps}
                            onChange={(e) => {
                              const val = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 1;
                              setPlanExercises(prev => prev.map((ex, i) =>
                                i !== exIndex ? ex : {
                                  ...ex,
                                  sets: ex.sets.map((s, j) =>
                                    j !== setIndex ? s : { ...s, targetReps: val }
                                  ),
                                }
                              ));
                            }}
                            inputProps={{
                              style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 'bold' },
                            }}
                            sx={{ flex: 1, '& input': { py: 1 } }}
                            size="small"
                          />
                          <Fab
                            size="small"
                            color="secondary"
                            onClick={() => adjustReps(exIndex, setIndex, 1)}
                            sx={{ minWidth: 36, minHeight: 36, width: 36, height: 36 }}
                          >
                            <AddIcon fontSize="small" />
                          </Fab>
                          <Typography variant="caption" color="text.secondary" sx={{ visibility: 'hidden' }}>кг</Typography>
                        </Box>
                      </Box>
                    ))}

                    {/* Add set button */}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddSet(exIndex)}
                    >
                      Добави сет
                    </Button>
                  </Stack>
                        </Paper>
                      )}
                    </SortableExerciseCard>
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>

            {/* Add exercise button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddExercise}
              sx={{ mt: 2 }}
            >
              Добави упражнение
            </Button>
          </>
        )}
      </Box>

      {/* Bottom save button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 2,
        }}
      >
        <Button
          variant="contained"
          color="success"
          fullWidth
          size="large"
          onClick={handleSavePlan}
          disabled={saving || planExercises.length === 0}
          startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
          sx={{ py: 1.5 }}
        >
          {saving ? 'Запазване...' : 'Запази план'}
        </Button>
      </Box>

      {/* Variation Switcher Dialog */}
      <Dialog open={variationDialogOpen} onClose={() => setVariationDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Смени вариация</DialogTitle>
        <DialogContent>
          {loadingVariationOptions ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography>Зареждане...</Typography>
            </Box>
          ) : variationOptions.length === 0 ? (
            <Typography sx={{ py: 2 }} color="text.secondary">Няма налични вариации.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {variationOptions.map((v) => {
                const currentEx = planExercises[variationExerciseIndex];
                const isCurrent = v.id === currentEx?.exerciseId;
                return (
                  <Card
                    key={v.id}
                    sx={{
                      cursor: isCurrent ? 'default' : 'pointer',
                      '&:hover': isCurrent ? {} : { backgroundColor: 'action.hover' },
                      border: isCurrent ? '2px solid' : '1px solid',
                      borderColor: isCurrent ? 'primary.main' : 'divider',
                      backgroundColor: isCurrent ? 'action.selected' : undefined,
                    }}
                    onClick={() => !isCurrent && handleSelectVariation(v)}
                  >
                    <CardContent sx={{ p: '12px !important', '&:last-child': { pb: '12px !important' } }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {v.variationName || v.name}
                        {isCurrent && ' (текущо)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.equipment} | {v.difficulty}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariationDialogOpen(false)}>Затвори</Button>
        </DialogActions>
      </Dialog>

      {/* Substitute Dialog */}
      <ExercisePickerDialog
        open={substituteDialogOpen}
        title="Замени упражнение"
        description="Избери упражнение за замяна в плана"
        confirmLabel="Потвърди"
        initialMuscleGroup={
          substituteExerciseIndex !== null
            ? planExercises[substituteExerciseIndex]?.muscleGroup || undefined
            : undefined
        }
        onConfirm={handleConfirmSubstitute}
        onClose={() => { setSubstituteDialogOpen(false); setSubstituteExerciseIndex(null); }}
      />

      {/* Add Exercise Dialog */}
      <ExercisePickerDialog
        open={addExerciseDialogOpen}
        title="Добави упражнение"
        description="Избери ново упражнение за добавяне към плана"
        confirmLabel="Добави"
        onConfirm={handleConfirmAddExercise}
        onClose={() => setAddExerciseDialogOpen(false)}
      />
    </Box>
  );
};

export default WorkoutPlanner;
